import { OrganizationsState, TileDescriptionsState } from "../../../store/types";
import { definedMapWithDefault } from "collection-utils";
import { NavItem } from "./breadcrumb/nav-breadcrumb";
import { getAppFacilities } from "webapp/lib/app-renderer";
import {
    AppAnalytics,
    UserDataAndState,
    isUserData,
    AuthenticationMethod,
    Owner,
    SubscriptionDiscount,
} from "common/Database";
import { formatShortDate } from "common/components/localization";
import { getAppsSaver } from "../../../lib/apps-saver";
import { getBuilderAppDatabase } from "../../../lib/web-database";
import * as H from "history";
import { app } from "../../../routes";
import { clearUndo } from "../../../lib/undo";
import * as appsActions from "../../../actions/apps-actions";
import { sleep, maybeAsync, assertNever } from "common/Support";
import {
    OrgMember as OrgMemberType,
    OrgDetails as OrgDetailsType,
    PlanDescription as PlanDescriptionType,
    BuilderTileDescription as BuilderTileDescriptionType,
    TitledRange as TitledRangeType,
    OrgWithNumEmployees,
} from "./orgs-lib-types";
import {
    ApplyPromoCodeBody,
    CapturePromoCodeResult,
    CreateOrganizationBody,
    PreviewChargeBody,
    PreviewChargeResponse,
    previewChargeResponse,
    SetPublishingActiveBody,
    setPublishingActiveResponseBody,
    SetPublishingActiveResponseBody,
} from "common/firebase-function-types";
import { isRight } from "fp-ts/lib/Either";
import { EminenceFlags } from "common/Database/eminence";
import { SubscriptionType } from "common/app-plans";
import { logError, logInfo } from "common/debug-print";
import { getFeatureSetting } from "common/feature-settings";

export type OrgMember = OrgMemberType;
export type OrgDetails = OrgDetailsType;
export type PlanDescription = PlanDescriptionType;
export type BuilderTileDescription = BuilderTileDescriptionType;
export type TitledRange = TitledRangeType;

export function makeNavBreadcrumbItems(
    currentLocation: string | undefined,
    orgID: string | undefined,
    orgs: OrganizationsState
) {
    const navItems: NavItem[] = [];
    if (!isUserOrgID(orgID)) {
        const org = orgs.find(o => o.id === orgID);
        const orgName = definedMapWithDefault(org, "Unknown", o => o.displayName);
        navItems.push({ name: orgName, target: `/o/${orgID}` });
    } else {
        navItems.push({ name: "My Apps", target: "/o/0" });
    }

    if (currentLocation !== undefined) {
        navItems.push({ name: currentLocation, target: undefined });
    }

    return navItems;
}

export function isUserOrgID(id: string | undefined): boolean {
    return id === undefined || id === "0";
}

export function getOrgOwner(orgMembers: readonly OrgMember[]): OrgMember | undefined {
    return orgMembers.length > 0 ? orgMembers[0] : undefined;
}

function mapJsonToOrgMember(json: any): OrgMember {
    return {
        userID: json.userID,
        displayName: json.displayName,
        email: json.email,
        photoURL: json.photoURL,
        status: json.status ?? "active",
        role: json.role,
    };
}

export async function createOrg(orgDetails: OrgDetails | OrgWithNumEmployees): Promise<string> {
    try {
        const { name } = orgDetails;
        const f = getAppFacilities();
        const createOrgBody: CreateOrganizationBody = {
            displayName: name,
            inviteeEmails: [],
            companyInformation: {
                industryKind: "industry" in orgDetails ? orgDetails.industry : "Individual",
                numAppUsers: { min: 0, max: 0 },
                numAppEditors: { min: 0, max: 0 },
                numEmployees: "numEmployees" in orgDetails ? orgDetails.numEmployees : { min: 0, max: 0 },
            },
        };
        const result = await f.callAuthCloudFunction("createOrganization", createOrgBody);
        const json = await result?.json();
        if (json !== undefined) {
            return json.organizationID;
        }
    } catch {
        // do nothing
    }
    return "0";
}

export async function getOrgMembers(orgID: string | undefined, retry = 0): Promise<readonly OrgMember[] | undefined> {
    if (isUserOrgID(orgID)) return undefined;

    try {
        const f = getAppFacilities();
        const result = await f.callAuthIfAvailableCloudFunction(
            "getOrganizationMembers",
            { organizationID: orgID },
            {}
        );
        if (result !== undefined) {
            const json = await result.json();
            return json.members.map(mapJsonToOrgMember);
        }
        return undefined;
    } catch {
        if (retry < 5) {
            await sleep(5000);
            return await getOrgMembers(orgID, retry + 1);
        }
    }
    return undefined;
}

export async function getOrganizationPersistentInviteLink(
    orgID: string | undefined,
    createNew = false
): Promise<string | undefined> {
    if (isUserOrgID(orgID)) return undefined;

    try {
        const f = getAppFacilities();
        const result = await f.callAuthCloudFunction("getOrganizationPersistentInviteLink", {
            organizationID: orgID,
            createNew,
        });
        if (result !== undefined) {
            const json = await result.json();
            return json.persistentInviteLink;
        }
        return undefined;
    } catch {
        return undefined;
    }
}

export async function addOrgMember(
    orgID: string | undefined,
    email: string | string[]
): Promise<readonly OrgMember[] | undefined> {
    if (isUserOrgID(orgID)) return undefined;
    const emails = typeof email === "string" ? [email] : email;

    try {
        const f = getAppFacilities();
        const result = await f.callAuthCloudFunction("inviteToOrganization", {
            organizationID: orgID,
            inviteeEmails: emails,
        });
        if (result !== undefined) {
            const json = await result.json();
            return json.members.map(mapJsonToOrgMember);
        }
        return undefined;
    } catch {
        return undefined;
    }
}

export async function removeOrgMember(orgID: string | undefined, member: OrgMember): Promise<boolean> {
    if (isUserOrgID(orgID)) return false;

    try {
        const f = getAppFacilities();
        const result = await f.callAuthCloudFunction("removeFromOrganization", {
            organizationID: orgID,
            userID: member.userID,
            userEmail: member.email,
        });
        return result?.ok ?? false;
    } catch {
        return false;
    }
}

export async function setOrgAdmin(orgID: string | undefined, member: OrgMember): Promise<boolean> {
    if (isUserOrgID(orgID)) return false;

    try {
        const f = getAppFacilities();
        const result = await f.callAuthCloudFunction("setOrgAdmin", {
            organizationID: orgID,
            userIDOrEmail: member.userID,
        });
        return result?.ok ?? false;
    } catch {
        return false;
    }
}

export async function deleteOrg(orgID: string): Promise<void> {
    try {
        const f = getAppFacilities();
        await f.callAuthCloudFunction("deleteOrganization", {
            organizationID: orgID,
        });
    } catch (e: unknown) {
        logError(e);
    }
}

export async function renameOrganization(organizationID: string, newName: string): Promise<void> {
    if (isUserOrgID(organizationID)) return undefined;

    try {
        logInfo("will call");
        const f = getAppFacilities();
        const response = await f.callAuthCloudFunction("renameOrganization", {
            organizationID,
            newName,
        });
        logInfo("got response", { response });
        return undefined;
    } catch (e: unknown) {
        logInfo({ e });
        return undefined;
    }
}

export interface BillingInfo {
    readonly canUpgrade: boolean;

    readonly basePrice: number;
    readonly pricePerUser: number;
    readonly pricePerApp: number;
    readonly totalPrice: number;

    readonly nextBillDate: string;

    readonly publicAppIDs: string[];
    readonly includedUsers: number;
    readonly includedApps: number;
    readonly additionalUsers: number;
}

export async function getBillingInfo(orgID: string | undefined, retry = 0): Promise<BillingInfo | undefined> {
    if (isUserOrgID(orgID)) return undefined;

    try {
        const f = getAppFacilities();
        const result = await f.callAuthCloudFunction("getOrganizationBilling", {
            organizationID: orgID,
        });
        const data = await result?.json();
        if (data !== undefined) {
            const billDate = new Date(data.nextBillDate * 1000);
            return {
                ...data,
                nextBillDate: formatShortDate(billDate, false),
            };
        }
    } catch {
        if (retry < 5) {
            await sleep(5000);
            return await getBillingInfo(orgID, retry + 1);
        }
    }

    return undefined;
}

export async function getPlansForOrg(orgID: string | undefined): Promise<PlanDescription[]> {
    if (isUserOrgID(orgID)) return [];

    try {
        const f = getAppFacilities();
        const result = await f.callAuthCloudFunction("getOrganizationPlans", {
            organizationID: orgID,
        });
        const data = await result?.json();
        return data;
    } catch {
        return [];
    }
}

const defaultTile: Omit<BuilderTileDescription, "appID"> = {
    title: "Loading…",
    primaryAccentColor: "#FFFFFF",
    authenticationKind: AuthenticationMethod.None,
    url: "#",
    analytics: undefined,
    isLoading: true,
};

export function toTileDescription(
    id: string,
    userID: string,
    tiles: TileDescriptionsState,
    loadTileDescriptionFromFirebase: (userID: string) => void
): BuilderTileDescription {
    const r = tiles[id];

    // This is an ugly way to side effect this. I hate every part of it.
    if (r === undefined) {
        loadTileDescriptionFromFirebase(userID);
    }
    return { appID: id, isLoading: false, ...(r === "loading" || r === undefined ? defaultTile : r) };
}

export function getUsageString(analytics: AppAnalytics | undefined, eminenceFlags: EminenceFlags, isPublic: boolean) {
    if (analytics === undefined || !eminenceFlags.isUserMetered) {
        if (isPublic) {
            return "Public app";
        } else {
            return "No users";
        }
    }
    let message = "A few users";
    const userCount = analytics.lastUniqueAppUsersInWindow;
    if (userCount === 0) {
        message = `No users`;
    } else if (userCount >= 10) {
        message = `${userCount.toLocaleString()} users`;
    }
    return message;
}

export async function openApp(
    appID: string,
    userData: UserDataAndState,
    history: H.History,
    unloadApps: typeof appsActions.unloadApps,
    path?: string
): Promise<void> {
    if (isUserData(userData) && !getFeatureSetting("readOnlyBuilder")) {
        if (
            !(await getAppsSaver().lockOrAskToForceBuilderAppLock(
                getBuilderAppDatabase(appID),
                userData.id,
                appID,
                false
            ))
        ) {
            return;
        }
    }
    // Here we have to reload the builder app, because it might be stale
    unloadApps([appID]);
    history.push(path ?? app(appID));
    clearUndo(appID);
}

export async function previewCharge(body: PreviewChargeBody): Promise<PreviewChargeResponse | undefined> {
    const res = await getAppFacilities().callAuthCloudFunction("previewCharges", body);

    if (res === undefined) return undefined;

    const charges = await maybeAsync(async () => {
        const result = await res.json();
        const decoded = previewChargeResponse.decode(result);
        return isRight(decoded) ? decoded.right : undefined;
    }, undefined);

    return charges;
}

export async function applyPromoCode(
    body: ApplyPromoCodeBody
): Promise<{ ownerSerial: number } | CapturePromoCodeResult | undefined> {
    const response = await getAppFacilities().callAuthCloudFunction("applyPromoCode", body);

    if (response === undefined || !response.ok) return undefined;

    return await maybeAsync(async () => {
        const { error, ownerSerial } = await response.json();
        if (error !== CapturePromoCodeResult.Ok) return error;
        return { ownerSerial };
    }, undefined);
}

export function getPromoCodeInfo(owner: Owner, subscriptionType: SubscriptionType): SubscriptionDiscount | undefined {
    if (subscriptionType === SubscriptionType.Monthly) {
        return owner.monthlySubscription?.appliedDiscount;
    }
    if (subscriptionType === SubscriptionType.Annual) {
        return owner.annualSubscription?.appliedDiscount;
    }
    return assertNever(subscriptionType);
}

export async function setAppIsFavorite(organizationID: string, appID: string, isFavorite: boolean): Promise<void> {
    if (isUserOrgID(organizationID)) return undefined;

    try {
        const f = getAppFacilities();
        await f.callAuthCloudFunction("setAppIsFavorite", {
            organizationID,
            appID,
            isFavorite,
        });
        return undefined;
    } catch {
        return undefined;
    }
}

export async function setIsPublishingActive(
    appID: string,
    active: boolean
): Promise<SetPublishingActiveResponseBody | undefined> {
    const body: SetPublishingActiveBody = {
        appID,
        active,
    };
    const res = await getAppFacilities().callAuthCloudFunction("setPublishingActive", body);

    if (res === undefined) return undefined;

    return await maybeAsync(async () => {
        const result = await res.json();
        const decoded = setPublishingActiveResponseBody.decode(result);
        return isRight(decoded) ? decoded.right : undefined;
    }, undefined);
}
